<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import draggable from 'vuedraggable'

import adminAPI from '@/services/api';
import { useNotificationStore, useClaimStore } from '@/store';
import { typeTranslations } from '@/utils/config/constants/documents-types';
import { openBoxFile } from '../../utils/open-box-folder';

import FilePreviewModal from "../files/FilePreviewModal.vue";
import BaseButton from '../ui/buttons/BaseButton.vue';
import { InformationCircleIcon, DocumentMagnifyingGlassIcon } from '@heroicons/vue/24/outline';

import type { File, ExpenseClaimDocument, RetrieveClaimWithRelationsResponse, ClaimDocuments } from '@/interfaces/data-contracts';
import { ArrowTopRightOnSquareIcon, ArrowUpOnSquareIcon } from '@heroicons/vue/24/solid';
import { MergeIcon } from 'lucide-vue-next';
import Alert from '../ui/alerts/Alert.vue';

const props = defineProps<{
  claim: RetrieveClaimWithRelationsResponse;
  legalSubpeona: any;
}>();

const claim = computed(() => props.claim);
const filesIn = ref<typeof claim.value.claimDocuments>([]);
const previewedFile = ref<{ data: string, file: File } | null>();

const drag = ref(false);
const transitionOptions = computed(() => { return { animation: 200, ghostClass: "ghost", }; });

watch(() => props.claim, async (newClaim, prevClaim) => {
  // Reset selected files when changing claim
  if (newClaim.id !== prevClaim?.id) filesIn.value = [];
}, { immediate: true });

watch(() => props.legalSubpeona, (legalSubpeona) => {
  // Display files in current formal notice project
  if (legalSubpeona) {
    filesIn.value = legalSubpeona.legalSubpoenaDocuments.map(({claimDocument}) => claimDocument);
  }
}, { immediate: true })

async function previewDocument(file: File) {
  const data = await adminAPI.retrieveFile(file.id)
  if (data) previewedFile.value = { data, file }
}

const tooltipFile = (document: ExpenseClaimDocument) => "Ajouté le " +
  format(new Date(document.createdAt), "d MMMM 'à' HH'h'mm", { locale: fr })


const loading = ref(false);

async function generateLegalSubpeonaProject() {
  if (!filesIn.value.length) {
    useNotificationStore().emitNotification({ title: "Aucun document sélectionné" })
    return;
  };
  loading.value = true;
  const waitNotification = useNotificationStore().emitNotification({
    type: "wait",
    title: "Le projet d'assignation est en train d'être générée",
    message: "Cela peut prendre de 1 à 2 minutes"
  }, 3500000);
  try {
    const { docxBoxFileId } = await adminAPI.generateLegalSubpoenaProject(props.claim.id, {
      claimDocumentsIds: filesIn.value.map(({ id }) => id)
    });
    useNotificationStore().emitNotification({
      title: "Projet d'assignation généré avec succès",
      type: "success",
    });
    openBoxFile(docxBoxFileId);
    // emits("update");
    // closeModalAddDocument();
  } catch (e) {

  } finally {
    loading.value = false;
    waitNotification.duration = 0;
  }
}

async function mergeLegalSubpeona() {
  loading.value = true;
  const waitNotification = useNotificationStore().emitNotification({
    type: "wait",
    title: "L'Assignation est en train d'être fusionnée",
    message: "Cela peut prendre de 1 à 3 minutes"
  }, 3500000);
  try {
    const res = await adminAPI.mergeLegalSubpoena(props.claim.id);
    // await fetchFormalNotice(props.claim.id);
    useNotificationStore().emitNotification({
      title: "Assignation finale générée avec succès",
      type: "success",
    });
    await useClaimStore().refreshClaim();
    // openBoxFile(docxBoxFileId);
    // emits("update");
    // closeModalAddDocument();
  } catch (e) {

  } finally {
    loading.value = false;
    waitNotification.duration = 0;
  }
}
</script>
<template>
    <div>
      <FilePreviewModal v-show="previewedFile?.data" @close="previewedFile = null" @cancel="previewedFile = null" :file="(previewedFile?.file as any)"
        :data="previewedFile?.data" />
      <div>
        <h3 class="mb-2 text-sm font-medium leading-7">Pièces dans l'Assignation</h3>
        <draggable group="claim-documents" :disabled="legalSubpeona" :list="filesIn"
          item-key="id" role="list" class="w-full col-span-3 mb-5 bg-white border border-blue-200 border-dashed divide-y rounded-md shadow-sm divide-blue-50"
          v-bind="transitionOptions"
          @start="drag = true"
          @end="drag = false"
        >
          <template #header>
            <li v-if="!filesIn.length" class="flex items-center justify-between py-4 pl-4 pr-5 text-sm leading-6">
            <div class="flex items-center flex-1 w-0">
              <InformationCircleIcon class="flex-shrink-0 w-5 h-5 ml-2 text-gray-400" aria-hidden="true" />
              <div class="flex flex-1 min-w-0 gap-2 ml-4">
                <span class="flex items-center font-medium truncate">
                  Aucun document pour l'instant
                </span>
                <div class="ml-auto">
                  <!-- <BaseButton @click="modalAddDocument = true" text="Ajouter" /> -->
                </div>
              </div>
            </div>
          </li>
          </template>
          <template #item="{ element, index }: { element: ClaimDocuments[number], index: number }">
            <li class="flex items-center justify-between py-4 pr-5 text-sm leading-6 list-group-item">
              <!-- <pre>{{ element }}</pre> -->
              <div class="flex items-center flex-1 w-0 cursor-pointer" @click="previewDocument(element.file)">
                <!-- <PaperClipIcon class="flex-shrink-0 w-5 h-5 text-gray-400" aria-hidden="true" /> -->
                <div class="flex flex-1 min-w-0 gap-2 ml-4">
                  <!-- <span class="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 rounded-full bg-gray-50 ring-1 ring-inset ring-gray-500/10"> -->
                  <!-- <span class="inline-flex items-center px-2 py-1 text-xs font-medium text-yellow-800 rounded-full bg-yellow-50 ring-1 ring-inset ring-yellow-600/20"> -->
                  <!-- <span class="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-700 rounded-full bg-purple-50 ring-1 ring-inset ring-purple-700/10"> -->
                  <span class="inline-flex items-center px-2 py-1 text-xs font-medium text-pink-700 rounded-full bg-pink-50 ring-1 ring-inset ring-pink-700/10">
                    Pièce № {{ index+1 }}
                  </span>
                  <span class="font-medium truncate">
                    {{ element.documentType.type === "OTHER" ? element.file.name : typeTranslations[element.documentType.type] }}
                  </span>
                </div>
              </div>
              <div class="flex flex-shrink-0 ml-4 space-x-4" v-permission="['UPCLAIM_ADMIN', 'LAWYER']">
                <button type="button" v-tooltip="tooltipFile(element as any)" @click="previewDocument(element.file)"
                  class="font-medium text-blue-600 rounded-md hover:text-blue-500">
                  <DocumentMagnifyingGlassIcon class="w-5 h-5" />
                </button>
                <!-- <span class="text-gray-200" aria-hidden="true">|</span>
                <button type="button" class="font-medium text-gray-900 rounded-md hover:text-gray-800"
                  @click="removeClaimDocument(element.id)"
                >
                  <TrashIcon class="w-5 h-5" />
                </button> -->
              </div>
            </li>
          </template>
        </draggable>
      </div>

      <div>
        <div v-if="!props.legalSubpeona" v-tooltip="'Les pièces sont en bêta (non terminé)'">
            <BaseButton type="submit" :loading="loading" text="Générer le projet d'assignation" @click="generateLegalSubpeonaProject" />
        </div>
        <div v-else class="inline-flex">
          <div class="mr-4">
            <BaseButton type="submit" :loading="loading" text="Fusionner le document final" @click="mergeLegalSubpeona" :icon="(MergeIcon as any)" />
          </div>
          <div>
            <BaseButton text="Ouvrir le fichier .docx" :icon="ArrowTopRightOnSquareIcon" @click="openBoxFile(legalSubpeona.docxFile.boxFileId)" />
          </div>
        </div>
      </div>
    </div>
</template>